import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';

export interface CollectionWorkflow {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  workflow_type: 'automatic' | 'manual' | 'hybrid';
  trigger_conditions: any;
  steps: any[];
  escalation_rules: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCollectionWorkflowData {
  name: string;
  description?: string;
  workflow_type: 'automatic' | 'manual' | 'hybrid';
  trigger_conditions: any;
  steps: any[];
  escalation_rules: any;
  is_active?: boolean;
}

export const useCollectionWorkflows = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const workflowsQuery = useQuery({
    queryKey: ['collection-workflows', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collection_workflows')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as CollectionWorkflow[];
    },
    enabled: !!user?.id,
  });

  const createWorkflowMutation = useMutation({
    mutationFn: async (workflowData: CreateCollectionWorkflowData) => {
      const { data, error } = await supabase
        .from('collection_workflows')
        .insert([{
          ...workflowData,
          user_id: user?.id,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-workflows'] });
      toast({
        title: "Workflow created",
        description: "Collection workflow has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create workflow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CreateCollectionWorkflowData> }) => {
      const { data, error } = await supabase
        .from('collection_workflows')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-workflows'] });
      toast({
        title: "Workflow updated",
        description: "Collection workflow has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update workflow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteWorkflowMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('collection_workflows')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-workflows'] });
      toast({
        title: "Workflow deleted",
        description: "Collection workflow has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete workflow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    workflows: workflowsQuery.data || [],
    isLoading: workflowsQuery.isLoading,
    error: workflowsQuery.error,
    createWorkflow: createWorkflowMutation.mutate,
    updateWorkflow: updateWorkflowMutation.mutate,
    deleteWorkflow: deleteWorkflowMutation.mutate,
    isCreating: createWorkflowMutation.isPending,
    isUpdating: updateWorkflowMutation.isPending,
    isDeleting: deleteWorkflowMutation.isPending,
  };
};